// The Configure tab's sections. Presentational: each takes the one form's
// `control` and owns no state or save of its own, the way
// `template-sections.tsx` does for the template editor.
//
// Layout, since it is the point of this file: `layout="split"` puts the label
// in a 12rem column with the control beside it, so a field is one row instead
// of a label, a control and a help line stacked three deep. Controls are
// capped rather than stretched — a name field 1400px wide is not a better name
// field, and the number knobs need four characters.

import { Button, FormSection, Input, Select, Switch } from "@pej/kore/components";
import { Link } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import type { Control } from "react-hook-form";
import type { FilterMode, FilterProperty, Template } from "@/api/types";
import { Field, useFieldArray } from "@/lib/forms";
import { type ConfigureValues, NEW_FILTER_ROW, NO_TEMPLATE } from "./configure-schema";

type ConfigureControl = Control<ConfigureValues>;

const PROPERTY_LABELS: Record<FilterProperty, string> = {
    orderDeviceId: "Order device",
    transactionOperatorName: "Operator",
};

const MODE_LABELS: Record<FilterMode, string> = {
    include: "Include",
    exclude: "Exclude",
};

export interface GeneralSectionProps {
    control: ConfigureControl;
    workspaceId: string;
    templates: Template[];
    /** The template currently picked, for the link beside the select. */
    templateId: string;
}

export function GeneralSection({ control, workspaceId, templates, templateId }: GeneralSectionProps) {
    // Select.Value's built-in resolution stringifies the raw VALUE, not the
    // chosen item's label (kore's own select.test.tsx documents this), and our
    // values are template ids — so the trigger maps id -> name itself.
    const templateLabel = (value: unknown): string => {
        if (value == null || value === "" || value === NO_TEMPLATE) return "No template";
        return templates.find((template) => template.id === value)?.name ?? "No template";
    };

    return (
        <FormSection title="General" className="p-5">
            <Field
                name="name"
                control={control}
                label="Name"
                layout="split"
                required
                render={({ field }) => <Input {...field} className="max-w-md" />}
            />
            <Field
                name="active"
                control={control}
                label="Active"
                layout="split"
                description="Turn off to take this screen out of rotation without deleting it."
                render={({ field: { value, onChange, ...rest } }) => (
                    <Switch checked={value} onCheckedChange={onChange} {...rest} />
                )}
            />
            <Field
                name="templateId"
                control={control}
                label="Template"
                layout="split"
                description="A screen with no template uses the built-in look and wording."
                render={({ field: { value, onChange, name } }) => (
                    <div className="flex max-w-md items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger>
                                    <Select.Value>{templateLabel}</Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    <Select.Item value={NO_TEMPLATE}>No template</Select.Item>
                                    {templates.map((template) => (
                                        <Select.Item key={template.id} value={template.id}>
                                            {template.name}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        </div>
                        {templateId !== NO_TEMPLATE && (
                            <Button
                                variant="ghost"
                                size="sm"
                                render={
                                    <Link
                                        to="/w/$workspaceId/templates/$templateId"
                                        params={{ workspaceId, templateId }}
                                    />
                                }
                            >
                                Edit template
                            </Button>
                        )}
                    </div>
                )}
            />
        </FormSection>
    );
}

export function FiltersSection({ control }: { control: ConfigureControl }) {
    const { fields, append, remove } = useFieldArray({ control, name: "filters" });

    return (
        <FormSection
            title="Filters"
            className="p-5"
            // In the header, not at the foot: the foot of this card is where
            // the page's one Save used to be mistaken for a per-section one.
            actions={
                <Button type="button" variant="outline" size="sm" onClick={() => append(NEW_FILTER_ROW)}>
                    Add filter
                </Button>
            }
        >
            <p className="text-sm text-kore-text-muted">
                Which orders reach this screen. Separate several values with commas. An include filter with no values
                matches nothing; an exclude filter with no values matches everything.
            </p>
            {/* Rendered only when there are rows. An empty flex column still
                pays its gap, which left a blank band under the heading. */}
            {fields.length > 0 && (
                <div className="flex flex-col gap-3">
                    {fields.map((rowField, index) => {
                        // Three column headings, written once. Repeated per row
                        // they are noise — a screen with three filters said the
                        // same three words nine times — so only the first row
                        // renders them, and every later row carries the same
                        // name on the control itself rather than going unnamed.
                        const heading = index === 0;
                        return (
                            // Every cell is label + control and nothing else,
                            // which is what lets `items-end` line up the
                            // labels, the controls and the remove button. Help
                            // text under one cell only would drag the row
                            // apart: the cell bottoms align, so a description
                            // under Values pushes the two selects a line lower
                            // than the input beside them. Row help belongs to
                            // the section, above.
                            <div key={rowField.id} className="flex flex-row items-end gap-3">
                                <div className="w-44">
                                    <Field
                                        name={`filters.${index}.property`}
                                        control={control}
                                        label={heading ? "Property" : undefined}
                                        render={({ field: { value, onChange, name } }) => (
                                            <Select value={value} onValueChange={onChange} name={name}>
                                                <Select.Trigger aria-label={heading ? undefined : "Property"}>
                                                    <Select.Value>
                                                        {(v: unknown) =>
                                                            typeof v === "string" && v in PROPERTY_LABELS
                                                                ? PROPERTY_LABELS[v as FilterProperty]
                                                                : "Select a property"
                                                        }
                                                    </Select.Value>
                                                </Select.Trigger>
                                                <Select.Content>
                                                    <Select.Item value="orderDeviceId">Order device</Select.Item>
                                                    <Select.Item value="transactionOperatorName">Operator</Select.Item>
                                                </Select.Content>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="w-36">
                                    <Field
                                        name={`filters.${index}.mode`}
                                        control={control}
                                        label={heading ? "Mode" : undefined}
                                        render={({ field: { value, onChange, name } }) => (
                                            <Select value={value} onValueChange={onChange} name={name}>
                                                <Select.Trigger aria-label={heading ? undefined : "Mode"}>
                                                    <Select.Value>
                                                        {(v: unknown) =>
                                                            typeof v === "string" && v in MODE_LABELS
                                                                ? MODE_LABELS[v as FilterMode]
                                                                : "Select a mode"
                                                        }
                                                    </Select.Value>
                                                </Select.Trigger>
                                                <Select.Content>
                                                    <Select.Item value="include">Include</Select.Item>
                                                    <Select.Item value="exclude">Exclude</Select.Item>
                                                </Select.Content>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="max-w-md flex-1">
                                    <Field
                                        name={`filters.${index}.valuesText`}
                                        control={control}
                                        label={heading ? "Values" : undefined}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                aria-label={heading ? undefined : "Values"}
                                                placeholder="e.g. POS-1, POS-2"
                                            />
                                        )}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Remove filter row ${index + 1}`}
                                    onClick={() => remove(index)}
                                >
                                    <Trash2 aria-hidden="true" />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </FormSection>
    );
}

interface KnobProps {
    name: "count" | "cutoffMinutes" | "minimumMinutes" | "outlierCount";
    label: string;
    description: string;
    control: ConfigureControl;
}

function Knob({ name, label, description, control }: KnobProps) {
    return (
        <Field
            name={name}
            control={control}
            label={label}
            layout="split"
            description={description}
            required
            render={({ field: { value, onChange, ...rest } }) => (
                <Input
                    {...rest}
                    type="text"
                    inputMode="numeric"
                    className="max-w-24"
                    value={value}
                    onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9]/g, "");
                        onChange(cleaned === "" ? "" : Number(cleaned));
                    }}
                />
            )}
        />
    );
}

export function TuningSection({ control }: { control: ConfigureControl }) {
    return (
        <FormSection title="Tuning" className="p-5">
            <p className="text-sm text-kore-text-muted">
                How the estimate is computed from recent orders. Saving recomputes what the screens show.
            </p>
            <Knob
                name="count"
                label="Order count"
                description="How many recent orders feed the estimate (1–100)."
                control={control}
            />
            <Knob
                name="cutoffMinutes"
                label="Cutoff (minutes)"
                description="Ignore orders older than this (1–1440)."
                control={control}
            />
            <Knob
                name="minimumMinutes"
                label="Minimum (minutes)"
                description="Never display less than this (0–240)."
                control={control}
            />
            <Knob
                name="outlierCount"
                label="Outliers to drop"
                description="Extreme orders — the samples furthest from the typical value, fast or slow — excluded before averaging (0–99)."
                control={control}
            />
        </FormSection>
    );
}
